import { memo, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Stack } from "@altimateai/lego";
import DataTable from "@/components/tables/DataTable";
import { Paginate } from "@/components/paginate";
import { RelativeComponentLoader } from "@/components/loader";
import { DownloadTable } from "@/components/downloadTable";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { getGroupedOpportunities, downloadGroupedOpportunitiesCSV } from "@/helpers/apis";
import { useExplorerFiltersFromUrl } from "../../hooks";
import { GET_GROUPED_OPPORTUNITIES_DATA } from "../../constants";
import { GroupedDetailsCell } from "./cells";
import { MoneyCell, TimeCell } from "../opportunities/cells";
import { GetGroupedOpportunitiesDataResponse, GroupedOpportunitiesFilters } from "../../types";
import { TableHeaderType } from "@/components/tables/types";

export const GroupsTable = memo(() => {
  const {
    startDate,
    endDate,
    gridSort,
    gridSortDirection,
    effortFilter,
    resourceNameFilter,
    opportunityNameFilter,
    resourceTypeFilter,
    ownerFilter,
    statusFilter,
    userFilter,
    warehouseFilter,
    instanceFilter,
  } = useExplorerFiltersFromUrl();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const getFiltersData = () => {
    const params: GroupedOpportunitiesFilters = {
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
    };

    if (effortFilter.length > 0) params.effort = effortFilter;
    if (resourceNameFilter.length > 0) params.resource_name = resourceNameFilter;
    if (opportunityNameFilter.length > 0) params.opportunity_name = opportunityNameFilter;
    if (resourceTypeFilter.length > 0) params.resource_type = resourceTypeFilter;
    if (ownerFilter.length > 0) params.owner = ownerFilter;
    if (statusFilter.length > 0) params.status = statusFilter;
    if (userFilter.length > 0) params.user = userFilter;
    if (warehouseFilter.length > 0) params.warehouse = warehouseFilter;
    if (instanceFilter.length > 0) params.instance_id = instanceFilter;

    return params;
  };

  const { data, isFetching: isLoading } = useQuery({
    queryKey: [
      GET_GROUPED_OPPORTUNITIES_DATA,
      startDate,
      endDate,
      effortFilter,
      resourceNameFilter,
      opportunityNameFilter,
      resourceTypeFilter,
      ownerFilter,
      userFilter,
      statusFilter,
      warehouseFilter,
      instanceFilter,
    ],
    queryFn: () => getGroupedOpportunities(getFiltersData()),
  });

  const navigateToOpportunity = (item: GetGroupedOpportunitiesDataResponse) => {
    navigate(`/discover?tab=opportunities&opportunity_name=${item.opportunity_name}`);
  };

  const handleDownloadClick = async () => {
    const params = getFiltersData();
    await downloadGroupedOpportunitiesCSV(params);
  };

  const sortedItems = useMemo(() => {
    const items = data;
    if (!items) return [];
    return items
      .toSorted(
        (a, b) =>
          ((gridSortDirection || "desc") === "desc" ? -1 : 1) *
          ((a[
            (gridSort as keyof GetGroupedOpportunitiesDataResponse) || "cost_savings_max"
          ] as number) -
            (b[
              (gridSort as keyof GetGroupedOpportunitiesDataResponse) || "cost_savings_max"
            ] as number))
      )
      .slice(page * pageSize, (page + 1) * pageSize);
  }, [data, gridSort, gridSortDirection, page, pageSize]);

  const headers: TableHeaderType<GetGroupedOpportunitiesDataResponse>[] = [
    {
      label: "Details",
      uid: "details",
      id: 0,
      width: 350,
      render: params => {
        return (
          <GroupedDetailsCell
            resource_type={params.resource_type}
            opportunity_name={params.opportunity_name}
            description={params.description || ""}
            occurrences={params.occurrences}
          />
        );
      },
    },
    {
      label: "Max Money Savings",
      uid: "cost_savings_max",
      id: 1,
      width: 180,
      render: params => {
        return <MoneyCell value={params.cost_savings_max} />;
      },
      sort: {
        sortSearchKey: "sort",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      label: "Min Money Savings",
      uid: "cost_savings_min",
      id: 2,
      width: 160,
      render: params => {
        return <MoneyCell value={params.cost_savings_min} />;
      },
      sort: {
        sortSearchKey: "sort",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      label: "Max Time Savings",
      uid: "time_savings_max",
      id: 3,
      width: 160,
      render: params => {
        return <TimeCell value={params.time_savings_max} />;
      },
      sort: {
        sortSearchKey: "sort",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      label: "Min Time Savings",
      uid: "time_savings_min",
      id: 4,
      width: 160,
      render: params => {
        return <TimeCell value={params.time_savings_min} />;
      },
      sort: {
        sortSearchKey: "sort",
        sortOrderSearchKey: "sort_order",
      },
    },
  ];

  return (
    <Stack direction={"column"} gap={"lg"} align={"start"} className={"al-rounded-md al-relative"}>
      <div className={"al-absolute al-right-0"} style={{ top: "-3rem" }}>
        <DownloadTable handleDownload={handleDownloadClick} />
      </div>
      <div className={"al-w-full"}>
        {!isLoading ? (
          <>
            <DataTable
              onRowClick={row => navigateToOpportunity(row)}
              header={headers}
              data={{ items: sortedItems }}
              columnPickerPortal="opportunities-column-picker"
              defaultSort={{ sortParamValue: "cost_savings_max", order: "desc" }}
            />
            <Paginate
              itemCount={data?.length ?? 0}
              numPages={Math.ceil((data?.length || 0) / pageSize)}
              page={page}
              pageSize={pageSize}
              onPageClick={setPage}
              onPageSizeChange={setPageSize}
              showPageSize
            />
          </>
        ) : (
          <RelativeComponentLoader label={"Loading opportunities"} />
        )}
      </div>
    </Stack>
  );
});

GroupsTable.displayName = "GroupsTable";
